// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Auth API Calls
export const authAPI = {
  register: async (userData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, message: error.message };
    }
  },

  login: async (credentials) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });
      
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Error parsing JSON response:', jsonError);
        return { 
          success: false, 
          message: `Server error: ${response.status} ${response.statusText}` 
        };
      }
      
      if (!response.ok) {
        return {
          success: false,
          message: data.message || `Login failed (${response.status})`
        };
      }
      
      return data;
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: error.message || 'Connection error' };
    }
  },
};

// Submission API Calls
export const submissionAPI = {
  submit: async (username, userId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('username', username);
      formData.append('userId', userId);

      const response = await fetch(`${API_BASE_URL}/submissions/submit`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Submission error:', error);
      return { success: false, message: error.message };
    }
  },

  getSubmission: async (username) => {
    try {
      const response = await fetch(`${API_BASE_URL}/submissions/${username}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Fetch submission error:', error);
      return { success: false, message: error.message };
    }
  },

  downloadSubmission: async (username) => {
    try {
      const response = await fetch(`${API_BASE_URL}/submissions/download/${username}`, {
        method: 'GET',
      });

      if (!response.ok) {
        return { success: false, message: 'Failed to download file' };
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${username}_abstract.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, message: 'Download successful' };
    } catch (error) {
      console.error('Download error:', error);
      return { success: false, message: error.message };
    }
  },

  getSubmissionHistory: async (username) => {
    try {
      const response = await fetch(`${API_BASE_URL}/submissions/history/${username}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Fetch submission history error:', error);
      return { success: false, submissions: [], message: error.message };
    }
  },
};

// Registration API Calls
export const registrationAPI = {
  submit: async (username, userId, registrationData, paymentProofFile) => {
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('userId', userId);
      formData.append('participantCategory', registrationData.participantCategory);
      if (registrationData.transactionDetails) {
        formData.append('transactionDetails', registrationData.transactionDetails);
      }
      if (paymentProofFile) {
        formData.append('paymentProof', paymentProofFile);
      }

      const response = await fetch(`${API_BASE_URL}/registrations/submit`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, message: error.message };
    }
  },

  getRegistration: async (username) => {
    try {
      const response = await fetch(`${API_BASE_URL}/registrations/${username}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Fetch registration error:', error);
      return { success: false, message: error.message };
    }
  },

  downloadPaymentProof: async (username) => {
    try {
      const response = await fetch(`${API_BASE_URL}/registrations/download/${username}`, {
        method: 'GET',
      });

      if (!response.ok) {
        return { success: false, message: 'Failed to download payment proof' };
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${username}_payment_proof`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, message: 'Download successful' };
    } catch (error) {
      console.error('Download error:', error);
      return { success: false, message: error.message };
    }
  },

  viewPaymentProof: async (username) => {
    try {
      const response = await fetch(`${API_BASE_URL}/registrations/view/${username}`, {
        method: 'GET',
      });

      if (!response.ok) {
        return { success: false, message: 'Failed to view payment proof' };
      }

      // Get the file blob and open in new window
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      return { success: true, message: 'Payment proof opened' };
    } catch (error) {
      console.error('View error:', error);
      return { success: false, message: error.message };
    }
  },
};

// Accommodation API Calls
export const accommodationAPI = {
  submit: async (username, userId, accommodationData, paymentProofFile) => {
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('userId', userId);
      formData.append('checkInDate', accommodationData.checkInDate);
      formData.append('checkOutDate', accommodationData.checkOutDate);
      if (accommodationData.transactionDetails) {
        formData.append('transactionDetails', accommodationData.transactionDetails);
      }
      if (paymentProofFile) {
        formData.append('accommodationProof', paymentProofFile);
      }

      const response = await fetch(`${API_BASE_URL}/accommodations/submit`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Accommodation error:', error);
      return { success: false, message: error.message };
    }
  },

  getAccommodation: async (username) => {
    try {
      const response = await fetch(`${API_BASE_URL}/accommodations/${username}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Fetch accommodation error:', error);
      return { success: false, message: error.message };
    }
  },

  downloadPaymentProof: async (username) => {
    try {
      const response = await fetch(`${API_BASE_URL}/accommodations/download/${username}`, {
        method: 'GET',
      });

      if (!response.ok) {
        return { success: false, message: 'Failed to download payment proof' };
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${username}_accommodation_proof`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, message: 'Download successful' };
    } catch (error) {
      console.error('Download error:', error);
      return { success: false, message: error.message };
    }
  },

  viewPaymentProof: async (username) => {
    try {
      const response = await fetch(`${API_BASE_URL}/accommodations/view/${username}`, {
        method: 'GET',
      });

      if (!response.ok) {
        return { success: false, message: 'Failed to view payment proof' };
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      return { success: true, message: 'Payment proof opened' };
    } catch (error) {
      console.error('View error:', error);
      return { success: false, message: error.message };
    }
  },
};

// Health check
export const checkBackendHealth = async () => {
  try {
    const response = await fetch(`${API_BASE_URL.replace('/api', '')}/api/health`, {
      method: 'GET',
    });
    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error('Health check error:', error);
    return { success: false, message: error.message };
  }
};
